/**
 * Recipe box grid/list toggle. Persisted per device (localStorage), same shape as the
 * detail page's units preference (`utils/unitsPreference.ts`) — a small typed enum, a
 * storage key namespaced under `awwyeah:recipes:`, and a tolerant reader that falls back
 * to the default for anything missing or invalid rather than throwing.
 */
export type RecipesViewPreference = 'grid' | 'list';

export const VIEW_STORAGE_KEY = 'awwyeah:recipes:view';

/** Minimal `Storage` shape we depend on, so tests can pass a fake instead of real localStorage
 * (mirrors `utils/unitsPreference.ts` / `theme/theme.ts`). */
export type ViewStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function isRecipesViewPreference(
  value: string | null | undefined,
): value is RecipesViewPreference {
  return value === 'grid' || value === 'list';
}

/** Reads the persisted preference, falling back to `'grid'` (today's only view) for anything
 * missing/invalid. */
export function readStoredViewPreference(storage: ViewStorage): RecipesViewPreference {
  let raw: string | null = null;
  try {
    raw = storage.getItem(VIEW_STORAGE_KEY);
  } catch {
    // Storage can throw (private browsing, quota) — fall back silently.
    return 'grid';
  }
  return isRecipesViewPreference(raw) ? raw : 'grid';
}

export function writeStoredViewPreference(
  storage: ViewStorage,
  preference: RecipesViewPreference,
): void {
  try {
    storage.setItem(VIEW_STORAGE_KEY, preference);
  } catch {
    // Best-effort persistence; a failed write just means it reverts to 'grid' next launch.
  }
}
